import {
  DateRangeSelect,
  type DateRange,
} from '@/components/ui/date-range-select';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { type TableViewColumnDto } from '@ditto/shared';
import { useMemo, type FC } from 'react';
import { getColorForOption } from '../../utils/cellUtils';

export type ColumnFilter =
  | { columnId: string; type: 'options'; selectedOptionIds: string[] }
  | {
      columnId: string;
      type: 'date';
      from: string | undefined;
      to: string | undefined;
    };

interface TableViewFiltersProps {
  columns: TableViewColumnDto[];
  filters: ColumnFilter[];
  onFiltersChange: (filters: ColumnFilter[]) => void;
}

export const TableViewFilters: FC<TableViewFiltersProps> = ({
  columns,
  filters,
  onFiltersChange,
}) => {
  const filterableColumns = useMemo(
    () =>
      columns.filter(
        column =>
          column.type === 'date' ||
          (column.type === 'options' &&
            column.options &&
            column.options.length > 0)
      ),
    [columns]
  );

  // Sort: active filters first, then inactive
  const sortedFilterColumns = useMemo(() => {
    const isActive = (col: TableViewColumnDto) => {
      const filter = filters.find(f => f.columnId === col.id);
      if (!filter) return false;
      if (filter.type === 'options') return filter.selectedOptionIds.length > 0;
      return !!(filter.from || filter.to);
    };

    const active = filterableColumns.filter(isActive);
    const inactive = filterableColumns.filter(col => !isActive(col));

    return [...active, ...inactive];
  }, [filterableColumns, filters]);

  const handleFilterChange = (
    columnId: string,
    update: ColumnFilter | null
  ) => {
    if (!update) {
      onFiltersChange(filters.filter(f => f.columnId !== columnId));
      return;
    }

    const existingIndex = filters.findIndex(f => f.columnId === columnId);
    if (existingIndex !== -1) {
      const newFilters = [...filters];
      newFilters[existingIndex] = update;
      onFiltersChange(newFilters);
    } else {
      onFiltersChange([...filters, update]);
    }
  };

  const handleOptionChange = (
    columnId: string,
    selectedOptionIds: string[]
  ) => {
    if (selectedOptionIds.length === 0) {
      handleFilterChange(columnId, null);
    } else {
      handleFilterChange(columnId, {
        columnId,
        type: 'options',
        selectedOptionIds,
      });
    }
  };

  const handleDateChange = (columnId: string, dateRange: DateRange) => {
    if (!dateRange.from && !dateRange.to) {
      handleFilterChange(columnId, null);
    } else {
      handleFilterChange(columnId, {
        columnId,
        type: 'date',
        from: dateRange.from,
        to: dateRange.to,
      });
    }
  };

  const clearFilter = (columnId: string) => {
    handleFilterChange(columnId, null);
  };

  if (filterableColumns.length === 0) {
    return null;
  }

  // Helper to format date range for display
  const formatDateRange = (from?: string, to?: string) => {
    if (!from && !to) return null;
    const formatDate = (d: string) => {
      const date = new Date(d);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    };
    if (from && to) return `${formatDate(from)} - ${formatDate(to)}`;
    if (from) return `from ${formatDate(from)}`;
    return `until ${formatDate(to!)}`;
  };

  // Helper to render selected options content with color dots
  const renderSelectedOptionsContent = (
    column: TableViewColumnDto,
    selectedIds: string[]
  ) => {
    if (selectedIds.length === 0) return null;

    const selectedOptions = selectedIds
      .map(id => column.options?.find(o => o.id === id))
      .filter(Boolean);

    // If only 1 selected, show the option name with its color dot
    if (selectedOptions.length === 1) {
      const option = selectedOptions[0]!;
      return (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{
              backgroundColor: getColorForOption(option.id, columns),
            }}
          />
          <span>{option.value}</span>
        </span>
      );
    }

    // Show up to 3 color dots + count
    const dotsToShow = selectedOptions.slice(0, 3);
    const count = selectedIds.length;

    return (
      <span className="inline-flex items-center gap-1.5">
        {dotsToShow.map(option => (
          <span
            key={option!.id}
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{
              backgroundColor: getColorForOption(option!.id, columns),
            }}
          />
        ))}
        <span>{count} selected</span>
      </span>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {sortedFilterColumns.map(column => {
        if (column.type === 'date') {
          const currentFilter = filters.find(
            (f): f is Extract<ColumnFilter, { type: 'date' }> =>
              f.columnId === column.id && f.type === 'date'
          );
          const dateRange: DateRange = {
            from: currentFilter?.from,
            to: currentFilter?.to,
          };
          const isActive = !!(currentFilter?.from || currentFilter?.to);
          const activeLabel = formatDateRange(
            currentFilter?.from,
            currentFilter?.to
          );

          return (
            <FilterPopoverTrigger
              key={column.id}
              label={column.name}
              isActive={isActive}
              operator={isActive ? 'between' : undefined}
              activeContent={activeLabel}
              onClear={isActive ? () => clearFilter(column.id) : undefined}
            >
              <div className="p-2">
                <DateRangeSelect
                  value={dateRange}
                  onChange={range => handleDateChange(column.id, range)}
                  label={column.name}
                  placeholder={column.name}
                  embedded
                />
              </div>
            </FilterPopoverTrigger>
          );
        }

        const currentFilter = filters.find(
          (f): f is Extract<ColumnFilter, { type: 'options' }> =>
            f.columnId === column.id && f.type === 'options'
        );
        const selectedOptionIds = currentFilter?.selectedOptionIds ?? [];
        const isActive = selectedOptionIds.length > 0;

        const filterOptions: FilterOption[] =
          column.options?.map(option => ({
            label: option.value,
            value: option.id,
            color: getColorForOption(option.id, columns),
          })) ?? [];

        return (
          <FilterPopoverTrigger
            key={column.id}
            label={column.name}
            isActive={isActive}
            operator={
              isActive
                ? selectedOptionIds.length === 1
                  ? 'is'
                  : 'is any of'
                : undefined
            }
            activeContent={renderSelectedOptionsContent(
              column,
              selectedOptionIds
            )}
            onClear={isActive ? () => clearFilter(column.id) : undefined}
          >
            <FilterOptionList
              options={filterOptions}
              selected={selectedOptionIds}
              onChange={selected => handleOptionChange(column.id, selected)}
            />
          </FilterPopoverTrigger>
        );
      })}
    </div>
  );
};
